<template>
    <section class="main">
        <loader v-if="!configLoaded" />
        <h1 v-if="configLoaded && isDeviceLinked" class="intro">
            <html-with-links tag="span" class="intro__inner" :html="$t('INSTALL_APPS_WITH_FREE_DEV_ACCOUNT')" />
        </h1>
        <div v-if="configLoaded" class="row">
            <div class="col col_1">
                <template v-if="isDeviceLinked">
                    <important-message v-if="hasPremium" type="success">
                        {{ $t('PREMIUM_ALREADY_ACTIVE') }}
                    </important-message>

                    <table class="table">
                        <template v-if="!config.free_account?.appleid || showConfigure">
                            <tr>
                                <td class="cell">
                                    Apple account
                                </td>
                                <td class="td">
                                    <input class="input" v-model="appleid" />
                                </td>
                            </tr>
                            <tr>
                                <td class="cell">
                                    {{ $t('PASSWORD') }}
                                </td>
                                <td class="td">
                                    <input class="input" type="password" v-model="password" />
                                </td>
                            </tr>
                            <tr>
                                <td class="cell">
                                </td>
                                <td class="td">
                                    <html-with-links tag="span" class="hint"
                                        :html="$t('PLUS_PRIVACY_HINT', { privacy_href: '/info/privacy' })" />
                                </td>
                            </tr>
                            <tr>
                                <td class="cell">
                                    {{ $t('SEND_SMS_TO_VERIFY_ACCOUNT') }}
                                </td>
                                <td class="td">
                                    <yes-no v-model="isSMSAuth"
                                        :options="[{ label: $t('YES'), value: 1 }, { label: $t('NO'), value: 0 }]"
                                        fit-content />
                                    <html-with-links tag="span" class="hint hint_mt" :html="$t('SMS_AUTH_HINT')" />
                                </td>
                            </tr>
                            <tr>
                                <td class="cell"></td>
                                <td class="td">
                                    <btn class="configure-button" type="primary" :loading="configuring"
                                        :disabled="!appleid || !password" @click.native="configureFreeAccount">
                                        {{ $t('CONFIGURE_FREE_ACCOUNT') }}
                                    </btn>
                                </td>
                            </tr>
                        </template>
                        <template v-if="config.free_account?.appleid && !showConfigure">
                            <tr>
                                <td class="cell">
                                    Apple account
                                </td>
                                <td class="td">
                                    {{ config.free_account?.appleid }}
                                    <br />
                                    <btn class="configure-button" type="primary" @click.native="reconfigureAccount">
                                        {{ $t('RECONFIGURE_YOUR_ACCOUNT') }}
                                    </btn>
                                </td>
                            </tr>
                            <tr>
                                <td class="cell"></td>
                                <td class="td">
                                    <btn class="delete-account-button" type="negative" :loading="deletingAccount"
                                        @click.native="deleteAccount">
                                        {{ $t('DELETE_FREE_DEV_ACCOUNT') }}
                                    </btn>
                                </td>
                            </tr>
                        </template>
                        <tr v-if="config.free_account?.status_translated">
                            <td class="cell">
                                {{ $t('STATUS') }}
                            </td>
                            <td class="td">
                                <span v-html="config.free_account?.status_translated"></span>
                                <template v-if="config.free_account?.status === 'otp_required' && !configuring">
                                    <br />
                                    <btn class="configure-button" type="primary" :loading="sendingOtp"
                                        @click.native="sendOtp">
                                        {{ $t('ENTER_OTP_SHORT') }}
                                    </btn>
                                </template>
                            </td>
                        </tr>
                        <tr
                            v-if="config.free_account?.reconfiguration_at && config.free_account.reconfiguration_at !== 0">
                            <td class="cell">
                                {{ $t('MANDATORY_RECONFIGURATION_DATE') }}
                            </td>
                            <td class="td">
                                {{ new Date(config.free_account.reconfiguration_at * 1000).toLocaleString() }}<br />
                                <span class="hint hint_mt">{{ $t('MANDATORY_RECONFIGURATION_NOTE') }}</span>
                            </td>
                        </tr>
                    </table>

                    <!-- Free Developer Account Capability Check -->
                    <div class="dev-account-check-container">
                        <important-message v-if="currentDevice.can_use_free_developer_account === 'yes'" type="info">
                            <div v-html="$t('CURRENT_DEVICE_CAN_USE_FREE_DEV_ACCOUNT_NOTICE')"></div>
                        </important-message>

                        <important-message v-else-if="currentDevice.can_use_free_developer_account === 'no'"
                            type="notice">
                            <div v-html="currentDevice.can_use_free_developer_account_validation_error.translated">
                            </div>
                        </important-message>
                    </div>
                </template>
            </div>
            <aside class="sidebar col col_2">
                <p class="sidebar__text g-text" v-html="$t('FREE_DEV_ACCOUNT_PAGE_SIDEBAR', { href: '/my/faq' })"></p>
            </aside>
        </div>

        <!-- Connection modal -->
        <modal name="connection-dialog" :height="'auto'" :width="300" :adaptive="true" :click-to-close="false">
            <div class="connection-dialog">
                <div class="connection-dialog__content">
                    <p>{{ connectionDialogContent }}</p>
                </div>
                <div class="connection-dialog__buttons">
                    <button class="g-button" @click="handleCancelConnection">{{ $t('CANCEL') }}</button>
                    <a :href="shortcutUrl" class="g-button g-button_primary">{{ $t('CONNECT') }}</a>
                </div>
            </div>
        </modal>
    </section>
</template>

<script>
import Loader from '@/components/Loader';
import Btn from '@/components/Button.vue';
import YesNo from '@/components/YesNo.vue';
import HtmlWithLinks from '@/components/HtmlWithLinks.vue';
import ImportantMessage from '@/components/ImportantMessage.vue';
import { mapGetters } from 'vuex';

export default {
  components: {
    Loader,
    Btn,
    HtmlWithLinks,
    YesNo,
    ImportantMessage,
  },
  data() {
    return {
      loading: false,
      configuring: false,
      sendingOtp: false,
      showConfigure: false,
      deletingAccount: false,
      appleid: '',
      password: '',
      isSMSAuth: 0,
      showingConnectionPrompt: false,
      shortcutUrl: 'shortcuts://run-shortcut?name=connect%20to%20appdb&input=text&text=https%3A%2F%2Fappdb.to%2Fclose.html',
      connectionDialogContent: '',
      connectionCancelMessage: '',
      intentionallyNavigatingBack: false,
    };
  },
  computed: {
    ...mapGetters(['devicesLoaded', 'isDeviceLinked', 'hasPremium']),
    title() {
      return this.$t('INSTALL_CONFIGURE_YOUR_FREE_DEV_ACCOUNT');
    },
    configLoaded() {
      return !!this.config.model || (this.devicesLoaded && !this.isDeviceLinked);
    },
    config() {
      return this.$store.getters.config;
    },
    currentDevice() {
      return this.$store.getters.devices
        .find(device => device.lt === this.$store.getters.lt) || {};
    },
  },
  methods: {
    async handleCancelConnection() {
      // First hide the dialog but don't reset the flag yet
      this.$modal.hide('connection-dialog');

      try {
        const confirmCancel = await this.$confirm(
          this.connectionCancelMessage,
          { okText: this.$t('YES'), cancelText: this.$t('NO'), hidePage: true },
        );

        if (confirmCancel) {
          // User confirmed cancellation - now we can set the flag to false
          this.showingConnectionPrompt = false;
          // Also ensure we're not in configuring state
          this.configuring = false;

          // Set flag to indicate we're intentionally navigating back
          this.intentionallyNavigatingBack = true;

          // Inform backend that user cancelled the connection process
          try {
            await this.$api.editFreeDevAccount({
              appleid: this.config.free_account?.appleid,
              status: 'error',
            });
          } catch (error) {
            console.error('Error updating free account status:', error);
          }

          // Reload config to get final state
          await this.$store.dispatch('getConfig');
        } else {
          // User clicked No, show the connection dialog again
          console.log('User decided to continue the configuration, showing connection dialog again');
          this.$modal.show('connection-dialog');
        }
      } catch {
        // User clicked No (or closed dialog), show the connection dialog again
        console.log('User decided to continue the configuration, showing connection dialog again');
        this.$modal.show('connection-dialog');
      }
    },
    async configureFreeAccount() {
      if (!this.$store.getters.canInstallApps) {
        await this.$alert(this.$t('TO_CONFIGURE_FREE_ACCOUNT_VISIT_FROM_DEVICE', { device: this.config.nice_idevice_model }));
        return;
      }

      this.configuring = true;
      const result = await this.$api.editFreeDevAccount({
        appleid: this.appleid,
        password: this.password,
        isSMSAuth: this.isSMSAuth,
        status: this.config.free_account?.appleid ? 'new' : '',
      })
        .catch(e => e);

      if (result instanceof Error) {
        this.configuring = false;
        return;
      }

      this.handleStatus(this.appleid)
        .finally(() => {
          this.configuring = false;
        });
    },
    async handleStatus(appleid) {
      await Promise.all([
        this.$store.dispatch('getConfig'),
        this.$store.dispatch('getDevices'),
      ]);
      const { status } = this.config.free_account;

      if (status === 'ok') {
        // Hide connection dialog if it's open
        if (this.showingConnectionPrompt) {
          this.$modal.hide('connection-dialog');
          this.showingConnectionPrompt = false;
        }

        this.showConfigure = false;
        this.$notify({
          type: 'success',
          text: this.$t('FREE_ACCOUNT_CONFIGURED'),
        });
        return true;
      }

      if (status === 'error') {
        // Hide connection dialog if it's open
        if (this.showingConnectionPrompt) {
          this.$modal.hide('connection-dialog');
          this.showingConnectionPrompt = false;
        }

        this.$notify({
          type: 'error',
          text: this.config.free_account.status_translated,
        });
        return false;
      }

      // Handle waiting_for_connection status
      if (status === 'waiting_for_connection' && !this.showingConnectionPrompt) {
        this.showingConnectionPrompt = true; // Prevent multiple prompts

        // Set dialog content
        this.connectionDialogContent = this.$t('FREE_ACCOUNT_CONNECT_REQUIREMENT');
        this.connectionCancelMessage = this.$t('CONFIRM_CANCEL_FREE_ACCOUNT_CONFIGURATION');

        // Show the connection dialog with the link button
        this.$modal.show('connection-dialog');
      } else if (status !== 'waiting_for_connection' && this.showingConnectionPrompt) {
        // If status changed from waiting_for_connection, hide the dialog
        this.$modal.hide('connection-dialog');
        this.showingConnectionPrompt = false;
      }

      if (status === 'otp_required') {
        const otp = await this.$prompt(this.$t('ENTER_OTP'));

        // Validate OTP - ensure it's exactly 6 digits
        const trimmedOtp = otp.trim();
        if (!/^\d{6}$/.test(trimmedOtp)) {
          this.$notify({
            type: 'error',
            text: this.$t('OTP_MUST_BE_6_DIGITS'),
          });
          // Try again
          return this.handleStatus(appleid);
        }

        await this.$api.editFreeDevAccount({
          appleid,
          otp: trimmedOtp,
        });
      }

      return new Promise((resolve) => {
        setTimeout(() => {
          resolve(this.handleStatus(appleid));
        }, 1000);
      });
    },
    deleteAccount() {
      this.$confirm(this.$t('DELETE_FREE_DEV_ACCOUNT_CONFIRMATION'))
        .then(() => {
          this.deletingAccount = true;
          return this.$api.deleteFreeDevAccount();
        })
        .then(() => Promise.all([
          this.$store.dispatch('getConfig'),
          this.$store.dispatch('getDevices'),
        ]))
        .finally(() => {
          this.deletingAccount = false;
        });
    },
    sendOtp() {
      this.sendingOtp = true;
      this.handleStatus(this.config.free_account.appleid)
        .finally(() => {
          this.sendingOtp = false;
        });
    },
    reconfigureAccount() {
      if (!this.$store.getters.canInstallApps) {
        this.$alert(this.$t('TO_CONFIGURE_FREE_ACCOUNT_VISIT_FROM_DEVICE', { device: this.config.nice_idevice_model }));
        return;
      }

      if (this.config.free_account?.appleid && this.config.free_account.appleid.includes('@')) {
        this.appleid = this.config.free_account.appleid;
      }
      this.showConfigure = true;
    },
  },
  mounted() {
    const el = this.$route.hash && document.querySelector(this.$route.hash);
    if (el) {
      el.scrollIntoView();
    }
  },
  created() {
    // Check if account is in verifying status and start polling
    this.$store.dispatch('getConfig').then(() => {
      if (this.config.free_account) {
        if (this.config.free_account.status === 'otp_required') {
          // Automatically trigger OTP entry if status is OTP required
          this.sendOtp();
        } else if (this.config.free_account.status === 'waiting_for_connection'
                    || (this.config.free_account.status !== 'ok'
                        && this.config.free_account.status !== 'error')) {
          // Start checking status if it's in a verifying state or waiting for connection
          this.handleStatus(this.config.free_account.appleid);
        }
      }
    });
  },
  beforeRouteLeave(to, from, next) {
    // If we're intentionally navigating or configuration is complete, proceed without confirmation
    if (this.intentionallyNavigatingBack || !this.configuring) {
      return next();
    }

    // If a configuration is in progress, ask for confirmation
    this.$confirm(this.$t('FREE_ACCOUNT_LEAVE_CONFIRMATION'), { okText: this.$t('YES'), hidePage: true })
      .then(() => {
        // Clean up when user confirms leaving
        this.showingConnectionPrompt = false;
        this.configuring = false;
        next();
      })
      .catch(() => {
        next(false);
      });
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/variables";

.error-wrapper {
    display: flex;
    margin-bottom: 15px;
}

.error {
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    margin-right: 5px;
    flex-grow: 1;
    font-size: 14px;

    &_mb {
        margin-bottom: 20px;

        @include tablet {
            margin-bottom: 15px;
        }
    }
}

.main {
    display: block;
    flex-grow: 1;
}

.intro {
    &__inner {
        margin-right: 5px;
    }

    &__status {
        font-size: inherit;
        font-weight: 400;
    }
}

.row {
    display: flex;
    align-items: flex-start;

    @include notebook {
        flex-wrap: wrap;
    }
}

.col {
    &_1 {
        flex-grow: 1;
    }

    &_2 {
        flex: 0 0 192px;
        margin-left: 15px;

        @include notebook {
            margin-left: 0;
            margin-top: 15px;
        }
    }

    @include notebook {
        flex: 0 0 100%;
    }
}

.table {
    line-height: 18px;
    margin-bottom: 15px;

    tr:not(:first-child) td {
        padding-top: 10px;
    }
}

.td {
    padding-left: 40px;
}

.cell {
    width: 140px;
    flex: 0 0 140px;
    word-break: break-word;
}

.provided {
    padding-top: 15px;
    border-top: 1px solid #e5e5e5;
    display: flex;

    &_alone {
        flex-direction: column;

        .provided__support {
            max-width: 100%;
        }

        .provided__link {
            margin-top: 0;
        }
    }

    &__val {
        padding-left: 40px;
        font-weight: 500;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
    }

    &__link {
        color: var(--text-primary);
        display: flex;
        align-items: center;
        margin: 7px 0;
    }

    &__icon {
        margin-right: 5px;
    }

    &__support {
        font-size: 12px;
        font-weight: 400;
        max-width: 300px;
    }
}

.button {
    margin-left: 5px;
}

.sidebar {
    background: var(--secondary-bg);
    border-radius: 8px;
    padding: 10px;

    &__heading {
        font-weight: 500;
        margin-bottom: 10px;
    }

    &__text {
        &::v-deep {
            p:not(:last-child) {
                margin-bottom: 15px;
            }
        }
    }
}

.list {
    list-style: disc inside;
    margin-bottom: 15px;

    &__item {
        &:not(:last-child) {
            margin-bottom: 5px;
        }
    }
}

.buy {
    margin-bottom: 15px;
}

.buy:not(:first-child) {
    margin-top: 25px;
}

.cancel-button,
.configure-button {
    margin-left: 0;

    &:not(:first-child) {
        margin-top: 5px;
    }
}

.input {
    background: var(--tertiary-bg);
    border: 1px solid var(--tertiary-accent);
    border-radius: 2px;
    padding: 4px 6px;
    margin-left: auto;
    flex: 0 0 150px;
    width: 150px;
    font-size: 14px;
    font-family: "IBM Plex Sans", sans-serif;
    color: var(--text-primary);

    &::placeholder {
        font-family: "IBM Plex Sans", sans-serif;
        color: var(--text-primary);
    }
}

.hint {
    display: inline-block;
    font-size: 12px;
    font-weight: 400;
    max-width: 300px;
    line-height: 1.2;

    &_mt {
        margin-top: 10px;
    }
}

.warning {
    color: var(--configure-error-text);
    background: var(--bg-error);
    border-radius: 2px;
    padding: 3px 10px;
    text-align: center;
    line-height: 18px;
    margin-bottom: 20px;
}

.delete-account-button {
    margin-left: 0;
}

.dev-account-check-container {
    margin-top: 30px;
}

.button-container {
    display: flex;
    justify-content: center;
    margin-top: 15px;
}

.check-button {
    margin-left: 0;
}

/* Connection Dialog Styles */
.connection-dialog {
    padding: 15px;
    max-height: 100%;
    color: var(--text-primary);

    &__content {
        margin-bottom: 15px;
        white-space: pre-line;

        p {
            margin: 0;
            line-height: 1.4;
        }
    }

    &__buttons {
        display: flex;
        justify-content: flex-end;
        border-top: 1px solid var(--tertiary-accent);
        padding-top: 15px;
        margin-bottom: 0;

        button {
            margin-right: 10px;
        }

        a.g-button {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            text-decoration: none;
        }
    }
}

/* Override modal styles for proper theming */
::v-deep .vm--modal[name="connection-dialog"] {
    background-color: var(--bg);
    border: 2px solid var(--lighter-primary);
}
</style>

<!-- Global styles for the modal -->
<style lang="scss">
/* Target the connection-dialog modal container - try multiple selectors */
.vm--modal[data-modal="connection-dialog"],
.vm--modal[name="connection-dialog"],
.vm--container .vm--modal {
    background-color: var(--bg) !important;
    border: 2px solid var(--lighter-primary) !important;
    color: var(--text-primary) !important;
}

/* Ensure overlay has proper opacity */
.vm--overlay {
    background: rgba(0, 0, 0, var(--modal-overlay-opacity)) !important;
}
</style>
